import type { UnifiedAccessibilityState } from "./types";

/** Roles whose ARIA definition requires `aria-checked`. */
const CHECKED_ROLES = new Set(["radio", "checkbox", "switch"]);
/** Roles that support `aria-expanded`. */
const EXPANDED_ROLES = new Set(["button", "link", "menuitem", "tab", "checkbox", "switch"]);
/** Roles that support `aria-selected`. */
const SELECTED_ROLES = new Set(["tab"]);

/**
 * Translates `accessibilityState` into the ARIA attributes the element's role
 * allows, so web never emits invalid ARIA (ADR-025). Shared by the web
 * primitives that take `accessibilityState` (Pressable, Box). `role` is the role
 * that actually renders: with `asChild` it may be the child's own, and an element
 * with no `role` attribute has its implicit one.
 *
 * - `busy` → `aria-busy` on any role
 * - `checked` → `aria-checked` on radio, checkbox and switch, where it is
 *   required, so it defaults to `false`
 * - `expanded` → `aria-expanded` on roles that support it
 * - `selected` → `aria-selected` on tab only. A selected button or link means
 *   "pressed" or "current page" depending on the component, so the component
 *   states that itself (e.g. `platformProps({ pressed })`).
 *
 * `disabled` is not mapped here; each primitive owns it. Pressable follows its
 * `disabled` prop, which is what actually stops activation.
 */
export function getAriaState(
  role: string | null | undefined,
  state: UnifiedAccessibilityState | undefined,
) {
  const attrs: Record<string, boolean | "mixed"> = {};
  if (state?.busy != null) attrs["aria-busy"] = state.busy;
  if (role == null) return attrs;

  if (CHECKED_ROLES.has(role)) attrs["aria-checked"] = state?.checked ?? false;
  if (state?.expanded != null && EXPANDED_ROLES.has(role)) {
    attrs["aria-expanded"] = state.expanded;
  }
  if (state?.selected != null && SELECTED_ROLES.has(role)) {
    attrs["aria-selected"] = state.selected;
  }
  return attrs;
}

/**
 * `role="heading"` requires `aria-level` (ADR-025: never invalid ARIA). An
 * `h1`–`h6` element already has its level; anything else defaults to 2, the
 * level React Native's "header" carries no equivalent for. A caller's own
 * `aria-level` still wins, since it's spread after.
 */
export function getHeadingLevel(role: string | null | undefined, element: string) {
  return role === "heading" && !/^h[1-6]$/.test(element) ? { "aria-level": 2 } : {};
}
